import { SyntheticEvent, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Selector from "@/components/form/selector/Selector"
import Toggle from "@/components/form/toggle/Toggle"
import { colors } from "@/components/colors"
import { CHART_TYPE, ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import { usePdfBuilderStore, selectSelectedItem } from "@/stores/pdfBuilder"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import {
  getTemplateSessionDetailsCharts,
  resolveTemplateSessionDetailsChart,
} from "@/app/report/common/sections/sessionDetails"
import {
  MAX_CHART_SERIES,
  SESSION_DETAILS_VIEW_LABELS,
  SESSION_DETAILS_VIEW_ORDER,
  isSessionDetailsContainer,
  resolveSessionDetailsViews,
} from "@/app/report/pdf/charts/BuilderSessionDetails"

import type { ChartOptionsPanelProps } from "./types"
import {
  DecimalPlacesSelector,
  MetricSelector,
  MetricSeriesRows,
  SortSelectors,
  type SortCategory,
} from "./SharedWidgets"

const DEFAULT_METRIC_COLORS = [colors.blue[900], colors.green[700], colors.red[500]]

/**
 * Session Details 옵션 — 보기(세션/1분/5분/15분)는 팔레트 항목마다 정해져 있다. 인터벌 보기는 그릴 세션을 고르고,
 * 지표는 템플릿 시드 + 가감(최대 3) 하며 지표마다 [이름, 차트 종류, 색]을 둔다 (Chart by Player 와 같은 규칙, #75).
 * 값 표시·소수 자릿수·계산 방식은 리포트 템플릿의 Session Details 설정을 따른다.
 */
export default function SessionDetailsOptions(props: ChartOptionsPanelProps) {
  const { t } = useTranslation()
  const { item, onDataSourceChange, onOptionChange, metricOptions, translateKey } = props
  const { reportTemplate, reportMode } = usePdfDataContext()
  const isSessionMode = reportMode === ACTIVITY_SESSION_TYPE.SESSION
  // 컨테이너(2026-09-30 개편): 보기(세션·1분·5분·15분)를 토글로 켜고 끈다. 옛 단일 보기 아이템은 정해진 보기 하나
  const isContainer = isSessionDetailsContainer(item.options)
  const enabledViews = resolveSessionDetailsViews(item.options)
  const toggleView = (target: string, on: boolean) =>
    onOptionChange(
      "intervalViews",
      SESSION_DETAILS_VIEW_ORDER.filter((candidate) =>
        candidate === target ? on : enabledViews.includes(candidate),
      ),
    )
  const layoutOptions = [
    { value: "auto", label: t("layoutAuto", "Auto") },
    { value: "manual", label: t("layoutManual", "Manual") },
  ]
  const templateCharts = useMemo(
    () => getTemplateSessionDetailsCharts(reportTemplate),
    [reportTemplate],
  )
  const currentChart = useMemo(
    () =>
      resolveTemplateSessionDetailsChart(
        templateCharts,
        item.dataSource.sectionIndex,
        item.dataSource.chartIndex,
      ),
    [templateCharts, item.dataSource.sectionIndex, item.dataSource.chartIndex],
  )
  const chartOptions = useMemo(
    () =>
      templateCharts.map((chart, order) => ({
        value: `${chart.sectionIndex}:${chart.chartIndex}`,
        label: `${t("sessionDetails", "Session Details")} ${order + 1}`,
      })),
    [templateCharts, t],
  )

  const templateMetrics = useMemo(
    () =>
      (currentChart?.settings.yAxis.map((y) => y.metric) ?? ["totalDistance"]).slice(
        0,
        MAX_CHART_SERIES,
      ),
    [currentChart],
  )
  const metrics = (item.dataSource.metrics ?? templateMetrics).slice(0, MAX_CHART_SERIES)
  const metricColors = item.options.colors ?? []
  const getMetricColor = (index: number) =>
    metricColors[index] ??
    currentChart?.settings.yAxis.find((y) => y.metric === metrics[index])?.color ??
    DEFAULT_METRIC_COLORS[index % DEFAULT_METRIC_COLORS.length]
  const getChartType = (metricKey: string) =>
    item.options.metricChartTypes?.[metricKey] ??
    currentChart?.settings.yAxis.find((y) => y.metric === metricKey)?.chartType ??
    CHART_TYPE.BAR

  const latest = () => selectSelectedItem(usePdfBuilderStore.getState())
  const handleAddMetric = useCallback(
    (metricKey: string) => {
      const currentMetrics = latest()?.dataSource.metrics ?? templateMetrics
      const currentColors = [...(latest()?.options.colors ?? [])]
      while (currentColors.length < currentMetrics.length) {
        currentColors.push(
          DEFAULT_METRIC_COLORS[currentColors.length % DEFAULT_METRIC_COLORS.length],
        )
      }
      currentColors.push(DEFAULT_METRIC_COLORS[currentColors.length % DEFAULT_METRIC_COLORS.length])
      onDataSourceChange("metrics", [...currentMetrics, metricKey])
      onOptionChange("colors", currentColors)
    },
    [onDataSourceChange, onOptionChange, templateMetrics],
  )
  const handleRemoveMetric = useCallback(
    (metricKey: string) => {
      const currentMetrics = latest()?.dataSource.metrics ?? templateMetrics
      const removeIndex = currentMetrics.indexOf(metricKey)
      const newColors = [...(latest()?.options.colors ?? [])]
      if (removeIndex >= 0 && removeIndex < newColors.length) {
        newColors.splice(removeIndex, 1)
      }
      onDataSourceChange(
        "metrics",
        currentMetrics.filter((k) => k !== metricKey),
      )
      onOptionChange("colors", newColors)
    },
    [onDataSourceChange, onOptionChange, templateMetrics],
  )
  const handleChartTypeChange = (metricKey: string, chartType: string) =>
    onOptionChange("metricChartTypes", {
      ...(latest()?.options.metricChartTypes ?? {}),
      [metricKey]: chartType,
    })
  const handleColorChange = (index: number, color: string) => {
    const newColors = [...(latest()?.options.colors ?? [])]
    while (newColors.length <= index) {
      newColors.push(getMetricColor(newColors.length))
    }
    newColors[index] = color
    onOptionChange("colors", newColors)
  }

  // 값 표시 위치·정렬 — 템플릿이 기본, 아이템별 덮어쓰기 (2026-10-01 요청)
  const dataLabelOptions = [
    { value: "hideValues", label: t("hideValues", "Hide values") },
    { value: "insidePlots", label: t("insidePlots", "In chart") },
    { value: "abovePlots", label: t("abovePlots", "Above plots") },
    { value: "separateTable", label: t("separateTable", "Separate table") },
  ]
  const dataLabel = item.options.dataLabel ?? currentChart?.settings.dataLabel ?? "hideValues"
  const sortCategories: SortCategory[] = [
    {
      value: "default",
      label: t("sortDefaultOrder", "Default order"),
      directions: [{ value: "asc" as const, label: t("sortDefaultOrder", "Default order") }],
    },
    ...metrics.map((metricKey) => ({
      value: metricKey,
      label: translateKey(metricKey),
      directions: [
        { value: "asc" as const, label: t("lowToHigh", "Low to High") },
        { value: "desc" as const, label: t("highToLow", "High to Low") },
      ],
    })),
  ]
  const sortValue = {
    key: item.options.sortKey ?? "default",
    order: item.options.sortOrder ?? ("asc" as const),
  }

  // 인터벌에 그릴 세션은 Data Source 의 세션 선택(PropertyPanel)에서 고른다 (2026-10-01 요청)

  return (
    <>
      {/* 보기 토글 — 켜진 보기가 세션→1분→5분→15분 순으로 쌓인다. 옛 단일 보기 아이템(intervalViews 없음)도 같은 토글을
          보여 주고, 토글하는 순간 컨테이너(intervalViews)가 된다 (2026-10-01 보고: 토글이 사라져 보임) */}
      <div className="PropertyPanel__field">
        <label>{t("views", "Views")}</label>
        {SESSION_DETAILS_VIEW_ORDER.map((candidate) => (
          <div
            key={candidate}
            className="PropertyPanel__field PropertyPanel__field--toggle"
            style={{ marginBottom: 4 }}>
            <Toggle
              checked={enabledViews.includes(candidate)}
              onChange={(e) => toggleView(candidate, e.target.checked)}
            />
            <label>
              {/* "Show Session"·"Show 1 min" 꼴 (2026-10-01 요청) */}
              {`${t("show", "Show")} ${t(
                SESSION_DETAILS_VIEW_LABELS[candidate].key,
                SESSION_DETAILS_VIEW_LABELS[candidate].label,
              )}`}
            </label>
          </div>
        ))}
        {enabledViews.includes("session") && !isSessionMode && (
          <span className="PropertyPanel__field-desc">
            {t(
              "sessionViewNeedsSessionMode",
              "The session chart needs Session mode in Report Filters",
            )}
          </span>
        )}
      </div>
      {templateCharts.length > 1 && (
        <div className="PropertyPanel__field">
          <Selector
            slotProps={{ popper: { className: "PropertyPanel__menu" } }}
            label={t("chart", "Chart")}
            options={chartOptions}
            value={
              chartOptions.find(
                (option) =>
                  option.value === `${currentChart?.sectionIndex}:${currentChart?.chartIndex}`,
              ) || chartOptions[0]
            }
            onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
              if (!newVal) {
                return
              }
              const [sectionIndex, chartIndex] = newVal.value.split(":").map(Number)
              onDataSourceChange("sectionIndex", sectionIndex)
              onDataSourceChange("chartIndex", chartIndex)
              const nextChart = templateCharts.find(
                (chart) => chart.sectionIndex === sectionIndex && chart.chartIndex === chartIndex,
              )
              if (nextChart) {
                const seeded = nextChart.settings.yAxis.slice(0, MAX_CHART_SERIES)
                onDataSourceChange(
                  "metrics",
                  seeded.map((y) => y.metric),
                )
                onOptionChange(
                  "colors",
                  seeded.map((y) => y.color),
                )
                onOptionChange("metricChartTypes", {})
              }
            }}
            placeholder={t("select")}
          />
        </div>
      )}

      <MetricSelector
        label={t("metrics", "Metrics")}
        selectedMetrics={metrics}
        metricOptions={metricOptions}
        onAdd={handleAddMetric}
        onRemove={handleRemoveMetric}
        translateKey={translateKey}
        maxCount={MAX_CHART_SERIES}
      />

      <MetricSeriesRows
        label={t("series", "Series")}
        metrics={metrics}
        getChartType={getChartType}
        getColor={getMetricColor}
        onChartTypeChange={handleChartTypeChange}
        onColorChange={handleColorChange}
        translateKey={translateKey}
        // 최대값 — 아이템 스냅샷(null 은 자동) > 템플릿 수동 스케일 (2026-10-01 결정)
        getScale={(metricKey) => {
          const entry = item.options.metricScales?.[metricKey]
          if (entry !== undefined) {
            return entry
          }
          const axis = currentChart?.settings.yAxis.find((y) => y.metric === metricKey)

          return axis?.manualScaleActive && axis.manualScaleValue != null
            ? axis.manualScaleValue
            : null
        }}
        onScaleChange={(metricKey, value) =>
          onOptionChange("metricScales", {
            ...(item.options.metricScales ?? {}),
            [metricKey]: value,
          })
        }
      />

      {/* 소수점 — 아이템 값 > 템플릿 값 > 0.1 (2026-10-01 결정) */}
      <DecimalPlacesSelector
        value={item.options.decimalPlaces ?? (currentChart?.settings.decimalPlace || 1)}
        onChange={(value) => onOptionChange("decimalPlaces", value)}
      />

      {/* 값 표시 위치 — 없음 / 그래프 가운데 / 그래프 위 / 모아서 보기 (2026-10-01 요청: 빌더 전용) */}
      <div className="PropertyPanel__field">
        <Selector
          slotProps={{ popper: { className: "PropertyPanel__menu" } }}
          label={t("dataLabels", "Data labels")}
          options={dataLabelOptions}
          value={dataLabelOptions.find((o) => o.value === dataLabel) || dataLabelOptions[0]}
          onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
            if (newVal) {
              onOptionChange("dataLabel", newVal.value)
            }
          }}
          placeholder={t("select")}
        />
      </div>

      {/* 정렬 — 기본(세션·시간 순) 또는 지표별 Low-High/High-Low */}
      <SortSelectors
        label={t("sort", "Sort")}
        categories={sortCategories}
        value={sortValue}
        onChange={(next) => {
          onOptionChange("sortKey", next.key)
          onOptionChange("sortOrder", next.order)
        }}
      />

      <div className="PropertyPanel__field PropertyPanel__field--toggle">
        <Toggle
          checked={item.options.showLegend ?? true}
          onChange={(e) => onOptionChange("showLegend", e.target.checked)}
        />
        <label>{t("showLegend", "Show Legend")}</label>
      </div>
      {/* Layout — Auto: 한 아이템에 보기들을 쌓고 페이지 경계는 줄 단위로 넘김, Manual: 보기마다 아이템 하나 (컨테이너만) */}
      {isContainer && (
        <div className="PropertyPanel__field">
          <Selector
            slotProps={{ popper: { className: "PropertyPanel__menu" } }}
            label={t("layout", "Layout")}
            options={layoutOptions}
            value={
              layoutOptions.find((o) => o.value === (item.options.tableScale ?? "auto")) ||
              layoutOptions[0]
            }
            onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
              if (newVal) {
                onOptionChange("tableScale", newVal.value)
              }
            }}
            placeholder={t("select")}
          />
        </div>
      )}
    </>
  )
}
